/**
 * Custom Property Service
 * 
 * Stores and retrieves import metadata (activityId, importedAt) directly on
 * Outlook items using Office.js Custom Properties API.
 * 
 * This is the primary source of truth for "View Activity" — avoids reliance
 * on backend uniqueId lookups that can return the wrong activity.
 */

const PROP_ACTIVITY_ID = 'assistgo_activityId';
const PROP_IMPORTED_AT = 'assistgo_importedAt';

/**
 * Promise wrapper around Office.js callback-style loadCustomPropertiesAsync
 */
function loadCustomProperties(item) {
  return new Promise((resolve, reject) => {
    item.loadCustomPropertiesAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || 'Failed to load custom properties'));
      }
    });
  });
}

/**
 * Promise wrapper around CustomProperties.saveAsync
 */
function saveCustomProperties(customProps) {
  return new Promise((resolve, reject) => {
    customProps.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error?.message || 'Failed to save custom properties'));
      }
    });
  });
}

export class CustomPropertyService {
  /**
   * Read stored import data from the Outlook item.
   * Returns null if nothing is stored.
   */
  static async getImportData(item) {
    try {
      const customProps = await loadCustomProperties(item);
      const activityId = customProps.get(PROP_ACTIVITY_ID);

      if (!activityId) return null;

      return {
        isImported: true,
        activityId,
        importedAt: customProps.get(PROP_IMPORTED_AT) || null
      };
    } catch (error) {
      console.error('CustomPropertyService.getImportData failed:', error);
      return null;
    }
  }

  /**
   * Store import data on the Outlook item after a successful import.
   */
  static async setImportData(item, activityId) {
    try {
      const customProps = await loadCustomProperties(item);

      customProps.set(PROP_ACTIVITY_ID, activityId);
      customProps.set(PROP_IMPORTED_AT, new Date().toISOString());

      await saveCustomProperties(customProps);
    } catch (error) {
      console.error('CustomPropertyService.setImportData failed:', error);
    }
  }

  /**
   * Remove stored import data (useful if we ever need re-import support).
   */
  static async clearImportData(item) {
    try {
      const customProps = await loadCustomProperties(item);

      customProps.remove(PROP_ACTIVITY_ID);
      customProps.remove(PROP_IMPORTED_AT);

      await saveCustomProperties(customProps);
    } catch (error) {
      console.error('CustomPropertyService.clearImportData failed:', error);
    }
  }
}
